import {
  type MantineColorsTuple,
  type VariantColorsResolver,
  colorsTuple,
  createTheme,
  defaultVariantColorsResolver,
  parseThemeColor,
} from "@mantine/core";

/**
 * ブランド赤。Mantine 上の赤はこの 1 本だけにする（brand-primary / brand-red の両方がこれを指す）。
 *
 * primaryShade（=6）は全色に効くため、Tailwind の brand-red スケール（500 = #E2231A）をそのまま
 * 並べると filled が 1 段濃い #C91E16 になる。shade 6 が #E2231A、filled の hover（shade 7）が
 * #C91E16 になるよう 1 段ずらして並べ、空いた shade 5 には #F05854 と #E2231A の中間色を置く。
 * shade 8（#A71811）は確認ダイアログの danger、shade 7 は薄い地の文字色に使う。
 */
const brandRed: MantineColorsTuple = [
  "#FEEBEA",
  "#FCC9C6",
  "#F8A3A0",
  "#F47D79",
  "#F05854",
  "#E93B33",
  "#E2231A",
  "#C91E16",
  "#A71811",
  "#841310",
];

const ctaBlue: MantineColorsTuple = [
  "#E0F5FD",
  "#B8E5F8",
  "#8FD5F3",
  "#67C5EE",
  "#3EB5E9",
  "#049FD7",
  "#0388B9",
  "#02719B",
  "#015A7D",
  "#01435F",
];

const brandGray: MantineColorsTuple = [
  "#F4F5F6",
  "#E2E4E6",
  "#CBCED1",
  "#9DA2A8",
  "#82888F",
  "#6C747D",
  "#4A5159",
  "#363B41",
  "#2A2E33",
  "#1E2125",
];

/**
 * 大阪取引所の推薦者バッジ専用の色（旧ワイン色）。レノボ（brand-red）と並ぶため赤とは別の色にする。
 * 直書きの hex を散らさず、ここで名前を付けて 1 箇所で持つ。
 */
const osakaExchange = colorsTuple("#8B1A2B");

/**
 * 危険な操作（削除・停止・ログアウトなど取り消しにくい操作）の色。主色（brand-primary = #E2231A）と
 * 見分けられるよう、ブランド赤を 2 段濃くずらし shade 6 を #A71811 にする（filled の hover は #841310）。
 * 入力エラーやエラー表示の Alert はフォームのエラー表示と揃えるため Mantine 標準の red のまま使う。
 */
const danger: MantineColorsTuple = [
  "#FEEBEA",
  "#FCC9C6",
  "#F8A3A0",
  "#F47D79",
  "#F05854",
  "#C91E16",
  "#A71811",
  "#841310",
  "#6E100D",
  "#5C0D0A",
];

/**
 * 否定・停止の状態（不採用・非承認・停止中）の色。主色の赤と紛れないよう黒に近い濃いグレーにする。
 * shade 6 が Tailwind の status-negative（#1E2125）になるよう brandGray から並べる。
 */
const statusNegative: MantineColorsTuple = [
  "#F4F5F6",
  "#E2E4E6",
  "#CBCED1",
  "#9DA2A8",
  "#82888F",
  "#4A5159",
  "#1E2125",
  "#15171A",
  "#0E1012",
  "#08090A",
];

/**
 * AI 監査による拒否（AUDIT_BLOCKED）の警告色。Tailwind の status-blocked と同じ琥珀。
 * shade 6 が #B45309（Tailwind の DEFAULT）、shade 7 が薄い地の文字用の #92400E になるよう並べる。
 */
const statusBlocked: MantineColorsTuple = [
  "#FFFBEB",
  "#FEF3C7",
  "#FDE68A",
  "#FCD34D",
  "#FBBF24",
  "#F59E0B",
  "#B45309",
  "#92400E",
  "#78350F",
  "#451A03",
];

/**
 * `variant="light"` のとき文字色を shade 7 に上げる色。shade 6 の文字は薄い地の上で WCAG AA（4.5）を割る。
 * ブランド赤（意味ベースの brand-primary と、色そのものを指す brand-red）と、AI 監査拒否の琥珀。
 */
const LIGHT_TEXT_SHADE7_COLORS = new Set(["brand-primary", "brand-red", "status-blocked"]);

/**
 * `variant="light"`（薄い地＋色文字）でブランド赤を使うと、文字 #E2231A が薄い赤の地の上で
 * コントラスト約 4.0 となり WCAG AA（4.5）を割る。light のときだけ文字色を shade 7（#C91E16・
 * 地の上で約 4.9）に上げる。地の色と filled など他の variant は既定のまま。
 */
export const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  if (input.variant !== "light") return resolved;
  const parsed = parseThemeColor({
    color: input.color ?? input.theme.primaryColor,
    theme: input.theme,
  });
  if (
    parsed.isThemeColor &&
    LIGHT_TEXT_SHADE7_COLORS.has(parsed.color) &&
    parsed.shade === undefined
  ) {
    return {
      ...resolved,
      color: `var(--mantine-color-${parsed.color}-7)`,
      hoverColor: `var(--mantine-color-${parsed.color}-7)`,
    };
  }
  return resolved;
};

export const mantineTheme = createTheme({
  variantColorResolver,
  primaryColor: "brand-primary",
  primaryShade: { light: 6, dark: 6 },
  colors: {
    // 主色（ログイン後のアクセント）。用途で名前を引き、値はブランド赤と共有する。
    "brand-primary": brandRed,
    "brand-red": brandRed,
    "osaka-exchange": osakaExchange,
    "status-blocked": statusBlocked,
    "status-negative": statusNegative,
    danger,
    "cta-blue": ctaBlue,
    "brand-gray": brandGray,
  },
  fontFamily: '"Noto Sans JP", system-ui, -apple-system, sans-serif',
  headings: {
    fontFamily: '"Noto Sans JP", system-ui, sans-serif',
    fontWeight: "700",
  },
  defaultRadius: "md",
  cursorType: "pointer",
  focusRing: "auto",
  components: {
    Button: {
      defaultProps: { radius: "md" },
      styles: { root: { fontWeight: 600, letterSpacing: "0.01em" } },
    },
    Card: {
      defaultProps: { shadow: "sm", padding: "lg", radius: "lg", withBorder: true },
    },
    TextInput: { defaultProps: { radius: "md" } },
    Textarea: { defaultProps: { radius: "md" } },
    Select: { defaultProps: { radius: "md" } },
    MultiSelect: { defaultProps: { radius: "md" } },
    Table: {
      styles: {
        th: { fontSize: "var(--mantine-font-size-xs)", fontWeight: 600 },
      },
    },
    // デザイン規約: ログイン/ログアウト時などに出る通知トースト (いわゆるパンクズリスト) は
    // radius (角丸) 禁止で角を立てる。白地・細い枠・落ち着いた影 (globals.css と統一) の
    // フラットなデザインに揃え、左の色アクセント/種別アイコンは Mantine 標準を活かす。
    Notification: {
      // 閉じる操作は「トースト本体クリック / 右スワイプ」に一本化する（toast-gestures.ts）ため、
      // X ボタンは非表示にする。root は cursor: pointer でクリック可能なことを示す。
      defaultProps: { radius: 0, withBorder: true, withCloseButton: false },
      styles: {
        root: {
          backgroundColor: "var(--mantine-color-white)",
          borderColor: "var(--mantine-color-gray-2)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          paddingTop: "var(--mantine-spacing-md)",
          paddingBottom: "var(--mantine-spacing-md)",
          cursor: "pointer",
        },
        title: {
          fontWeight: 600,
          fontSize: "var(--mantine-font-size-sm)",
          color: "var(--mantine-color-brand-gray-9)",
        },
        description: {
          fontSize: "var(--mantine-font-size-sm)",
          color: "var(--mantine-color-brand-gray-6)",
        },
      },
    },
    Modal: { defaultProps: { radius: "lg" } },
    Menu: { defaultProps: { radius: "md" } },
  },
});
